import { type IAgentHierarchyNode, type IPeerThreadSummary } from '../interface/peer-chat';
import { clock, shortDate } from './peer-chat-copy';
import { type IInboxSortable, type InboxUnitState, previewOf, sortInboxRows } from './peer-inbox';

// The Messages inbox, as data. Rows are the AGENTS a parent supervises (or the one parent a
// child reports to), joined with whatever thread exists — never the threads alone, because the
// agent who has never been messaged is exactly who a parent needs to reach on election day.

/** One child entity of the parent, from the result-analytics ledger. */
export interface IInboxUnit {
	code: string;
	unitState: InboxUnitState;
	uploadedAt?: string | null;
	/** The ledger row's own flags, carried so the coverage drawer can be opened with them. */
	analytics?: { isUploaded?: boolean; hasConflict?: boolean; dateUploaded?: string };
}

/** Field-language section headers, in display order. Mirrors the admin desk's buckets. */
export type InboxSection = 'waiting' | 'talking' | 'quiet' | 'none';

export const SECTION_ORDER: InboxSection[] = ['waiting', 'talking', 'quiet', 'none'];

export const SECTION_LABEL: Record<InboxSection, string> = {
	waiting: 'Waiting on you',
	talking: 'Talking',
	quiet: 'Gone quiet',
	none: 'Not messaged yet',
};

export interface IInboxRow extends IInboxSortable {
	/** Core user id of the agent; the chat counterpart id. 0 when nobody covers the unit. */
	agentId: number;
	/** False for a unit with nobody assigned: the row opens the coverage gap, not a chat. */
	hasAgent: boolean;
	name: string;
	analytics?: IInboxUnit['analytics'];
	/** The agent's level label for the row eyebrow. */
	level: string;
	isActive: boolean;
	uploadedAt?: string | null;
	/** One line, "You: " when the reader wrote it; empty when there is no thread. */
	preview: string;
	summary: IPeerThreadSummary | null;
	section: InboxSection;
}

/** How long a thread may be silent before the parent should wonder. */
export const QUIET_AFTER_MS = 60 * 60_000;

export const NO_AGENT_NAME = 'No agent assigned';

export const sectionOf = (
	row: Pick<IInboxRow, 'unread' | 'summary' | 'lastMessageAt' | 'unitState'>,
	now = Date.now()
): InboxSection => {
	if (row.unread > 0) return 'waiting';
	if (!row.summary || !row.lastMessageAt) return 'none';
	const age = now - Date.parse(row.lastMessageAt);
	if (!Number.isFinite(age) || age <= QUIET_AFTER_MS) return 'talking';
	// A quiet thread whose unit is already in has nothing to chase.
	return row.unitState === 'uploaded' ? 'talking' : 'quiet';
};

export interface IBuildInboxInput {
	/**
	 * The parent's child UNITS, the spine of the list. Null when there is no unit list (a
	 * child's one-row inbox, or the ledger could not be read) — rows then come from `agents`.
	 */
	units: IInboxUnit[] | null;
	/** Who the reader may message: direct children (or the one parent). */
	agents: IAgentHierarchyNode[];
	summaryFor: (agentId: number) => IPeerThreadSummary | undefined;
	/** Live engine counts by counterpart id, when the panel is mounted beside hosted engines. */
	liveUnread?: ReadonlyMap<number, number>;
	me: number;
	now?: number;
}

const levelLabel = (level: string): string => {
	switch ((level ?? '').toLowerCase()) {
		case 'pollingunit':
			return 'Polling unit';
		case 'ward':
			return 'Ward';
		case 'lga':
			return 'LGA';
		case 'state':
			return 'State';
		default:
			return level || 'Agent';
	}
};

const levelOfCode = (code: string): string => {
	switch (code.split('-').length) {
		case 1:
			return 'State';
		case 2:
			return 'LGA';
		case 3:
			return 'Ward';
		default:
			return 'Polling unit';
	}
};

/**
 * Sorted rows, sections assigned. Pure.
 *
 * <p>Units first: a unit nobody covers is the gap the parent most needs to see, and a list
 * built from agents alone would hide it. An agent the ledger does not list a unit for (the
 * ledger read failed, or a reassignment raced it) still gets a row, with its state unknown.</p>
 */
export const buildInboxRows = ({
	units,
	agents,
	summaryFor,
	liveUnread,
	me,
	now = Date.now(),
}: IBuildInboxInput): IInboxRow[] => {
	const agentRow = (agent: IAgentHierarchyNode, unit: IInboxUnit | null): IInboxRow => {
		const summary = summaryFor(agent.userId) ?? null;
		const live = liveUnread?.get(agent.userId);
		const unread = typeof live === 'number' ? live : Math.max(0, summary?.unreadForMe ?? 0);
		const row = {
			agentId: agent.userId,
			hasAgent: true,
			name: agent.fullName || 'Agent',
			level: levelLabel(agent.level),
			code: unit?.code ?? agent.entityCode,
			isActive: agent.isActive,
			unitState: unit?.unitState ?? 'unknown',
			uploadedAt: unit?.uploadedAt ?? null,
			analytics: unit?.analytics,
			unread,
			lastMessageAt: summary?.lastMessageAt ?? null,
			preview: summary ? previewOf(summary, me) : '',
			summary,
		};
		return { ...row, section: sectionOf(row, now) };
	};

	const gapRow = (unit: IInboxUnit): IInboxRow => ({
		agentId: 0,
		hasAgent: false,
		name: NO_AGENT_NAME,
		level: levelOfCode(unit.code),
		code: unit.code,
		isActive: true,
		unitState: unit.unitState,
		uploadedAt: unit.uploadedAt ?? null,
		analytics: unit.analytics,
		unread: 0,
		lastMessageAt: null,
		preview: '',
		summary: null,
		section: 'none',
	});

	if (!units) return sortInboxRows(agents.map((agent) => agentRow(agent, null)));

	const byCode = new Map(agents.map((a) => [a.entityCode, a]));
	const seen = new Set<number>();
	const rows: IInboxRow[] = units.map((unit) => {
		const agent = byCode.get(unit.code);
		if (!agent) return gapRow(unit);
		seen.add(agent.userId);
		return agentRow(agent, unit);
	});
	for (const agent of agents) if (!seen.has(agent.userId)) rows.push(agentRow(agent, null));
	return sortInboxRows(rows);
};

export type InboxFilter = 'all' | 'pending' | 'conflict' | 'unread';

export const INBOX_FILTERS: { value: InboxFilter; label: string }[] = [
	{ value: 'all', label: 'All' },
	{ value: 'pending', label: 'Still pending' },
	{ value: 'conflict', label: 'Conflict' },
	{ value: 'unread', label: 'Unread' },
];

export const applyInboxFilter = (rows: IInboxRow[], filter: InboxFilter, search: string): IInboxRow[] => {
	const q = search.trim().toLowerCase();
	return rows.filter((row) => {
		if (filter === 'pending' && row.unitState !== 'pending') return false;
		if (filter === 'conflict' && row.unitState !== 'conflict') return false;
		if (filter === 'unread' && row.unread === 0) return false;
		if (q && !row.code.toLowerCase().includes(q) && !row.name.toLowerCase().includes(q)) return false;
		return true;
	});
};

/** Rows grouped under their header, empty sections omitted, in display order. */
export const groupBySection = (rows: IInboxRow[]): { section: InboxSection; rows: IInboxRow[] }[] =>
	SECTION_ORDER.map((section) => ({ section, rows: rows.filter((r) => r.section === section) })).filter((g) => g.rows.length > 0);

/**
 * The one serif line at the top: what the inbox is for. Counts only rows whose unit state is
 * KNOWN — an unknown state is not "pending", and a ledger that could not be read must not
 * produce "8 of 8 units still pending" out of eight agents. Null when nothing is countable.
 */
export const inboxSummaryLine = (rows: IInboxRow[], unitNoun = 'units'): string | null => {
	const known = rows.filter((r) => r.unitState !== 'unknown');
	if (known.length === 0) return null;
	const pending = known.filter((r) => r.unitState !== 'uploaded').length;
	if (pending === 0) return `All ${known.length} ${unitNoun} are in`;
	return `${pending} of ${known.length} ${unitNoun} still pending`;
};

/** Which filters need a unit state the list does not have yet. */
export const filterNeedsUnitState = (filter: InboxFilter): boolean => filter === 'pending' || filter === 'conflict';

/** Row time: `HH:MM` today, "Yesterday", else `12 Jul`. Empty when there is no thread. */
export const rowTime = (iso: string | null | undefined, now = Date.now()): string => {
	if (!iso) return '';
	const t = Date.parse(iso);
	if (!Number.isFinite(t)) return '';
	const date = new Date(t);
	const today = new Date(now);
	if (date.toDateString() === today.toDateString()) return clock(date);
	const yesterday = new Date(now - 24 * 60 * 60_000);
	if (date.toDateString() === yesterday.toDateString()) return 'Yesterday';
	return shortDate(date);
};

/** The composed accessible name of a row. */
export const rowAriaLabel = (row: IInboxRow, time: string): string => {
	const parts = [row.hasAgent ? row.name : NO_AGENT_NAME, row.code];
	if (row.unitState !== 'unknown') parts.push(row.unitState);
	if (row.unread > 0) parts.push(`${row.unread} unread`);
	if (time) parts.push(`last message ${time}`);
	return parts.join(', ');
};
